/**
 * RTK Query baseQuery with Bearer auth — shared across all RTK Query APIs.
 *
 * Reads the auth token from the Redux store (state.auth.token) and injects it
 * as `Authorization: Bearer <token>` on every outgoing request. The legacy
 * axios `api` client in `services/api.ts` does this via an axios interceptor;
 * RTK Query's `fetchBaseQuery` is independent and needs its own equivalent.
 *
 * Without this, RTK Query endpoints (e.g. `getWidgetCatalog`,
 * `batchCheckWidgetCompatibility`, `getDescriptorCatalog`, all dashboard
 * layout-preset endpoints) fire unauthenticated → backend returns 401 → the
 * picker renders an empty list ("No widgets match your search.") because the
 * data is never populated.
 */
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { RootState } from '@/features/store';
import {
  markBackendReachable,
  markBackendUnavailable,
} from '@/services/connectivity';

type AuthedFetchBaseQuery = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;

export function getAuthedBaseQuery(baseUrl: string) {
  const rawBaseQuery = fetchBaseQuery({
    baseUrl,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth?.token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  });

  return (async (args, api, extraOptions) => {
    const result = await rawBaseQuery(args, api, extraOptions);
    const status = result.error?.status;
    const transportFailure = status === 'FETCH_ERROR'
      || status === 'TIMEOUT_ERROR'
      || (typeof status === 'number' && status >= 500);

    if (transportFailure) {
      markBackendUnavailable();
    } else {
      // A successful response, or a server-generated 4xx, proves reachability.
      markBackendReachable();
      if (status === 401) {
        // RTK Query does not pass through the Axios refresh interceptor. Keep
        // explicit 401 invalidation consistent across both API clients while
        // preserving cached auth for transport failures and 403 responses.
        api.dispatch({ type: 'auth/clearAuth' });
      }
    }
    return result;
  }) as AuthedFetchBaseQuery;
}
